// src/api/websites.ts
/**
 * Frontend API client for websites — the configured entity that scans now hang
 * off. A website carries its own CMP choice and e-commerce funnel URLs; Verity
 * proposes both from a DISCOVERY scan the moment the website is created, and
 * the customer confirms or edits them in Settings.
 *
 * Talks to the BFF proxy at `/websites` (VITE_API_URL already includes the
 * `/api` prefix). Mirrors the fetch/error conventions of `websiteScans.ts`:
 * thin wrappers around `apiJson`, which throws `ApiError` (with `.status`) on
 * any non-2xx so pages can branch on the HTTP status (403/409/422/429).
 */
import { apiJson } from "./client";
import type { WebsiteScanKind, WebsiteScanListItem } from "./websiteScans";

// ---------------------------------------------------------------------------
// Enums / literals
// ---------------------------------------------------------------------------

/** Lifecycle of the automatic discovery scan that proposes CMP + funnel URLs. */
export type DiscoveryStatus = "PENDING" | "RUNNING" | "READY" | "FAILED";

/**
 * The CMP the scanner should assume. `auto` lets the runner detect it, `none`
 * declares the site has no consent banner; the rest pin a specific vendor.
 */
export type CmpSelection =
  | "auto"
  | "none"
  | "cookiebot"
  | "onetrust"
  | "didomi"
  | "usercentrics"
  | "complianz"
  | "termly"
  | "consentmanager"
  | "iubenda"
  | "consentstudio"
  | "tcf";

/** Customer-facing label per CMP value. Never show the raw slug. */
const CMP_LABELS: Record<CmpSelection, string> = {
  auto: "Auto-detect",
  none: "No CMP",
  cookiebot: "Cookiebot",
  onetrust: "OneTrust",
  didomi: "Didomi",
  usercentrics: "Usercentrics",
  complianz: "Complianz",
  termly: "Termly",
  consentmanager: "consentmanager",
  iubenda: "Iubenda",
  consentstudio: "Consent Studio",
  tcf: "TCF v2 (generic)",
};

/** Ordered options for the CMP `<Select>` — `auto` and `none` lead. */
export const CMP_OPTIONS: { value: CmpSelection; label: string }[] = (
  [
    "auto",
    "none",
    "cookiebot",
    "onetrust",
    "didomi",
    "usercentrics",
    "complianz",
    "termly",
    "consentmanager",
    "iubenda",
    "consentstudio",
    "tcf",
  ] as CmpSelection[]
).map((value) => ({ value, label: CMP_LABELS[value] }));

/**
 * Friendly label for a CMP value. Unknown values (a vendor added by the
 * backend before the frontend knows it) fall back to the raw value rather
 * than to an empty cell.
 */
export function cmpLabel(value: string | null | undefined): string {
  if (!value) return CMP_LABELS.auto;
  return CMP_LABELS[value as CmpSelection] ?? value;
}

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * How an e-commerce funnel scan reaches each step.
 *
 * A URL pins a page; a selector pins a CLICK on the page the walk is already
 * on. `checkout_url` and `checkout_selector` are mutually exclusive - the
 * backend answers 422 when both are set, because two ways to reach one step
 * with an implicit precedence rule is config that reads as one thing and does
 * another. `add_to_cart_selector` has no URL sibling: there is no add-to-cart
 * page, only a button.
 */
export type FunnelConfig = {
  product_url?: string | null;
  /** CSS selector for the add-to-cart control on the product page. */
  add_to_cart_selector?: string | null;
  cart_url?: string | null;
  checkout_url?: string | null;
  /** CSS selector for the checkout control. Excludes `checkout_url`. */
  checkout_selector?: string | null;
};

/**
 * What the discovery scan proposed. Null until the scan finishes; individual
 * fields stay absent when discovery could not find that step.
 */
export type DiscoveryResult = {
  cmp_detected?: string | null;
  cmp_slug?: string | null;
  funnel?: FunnelConfig | null;
};

export type WebsiteListItem = {
  public_id: string;
  url: string;
  host: string;
  name?: string | null;
  cmp_selected: CmpSelection | string;
  discovery_status: DiscoveryStatus | string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type WebsiteDetail = WebsiteListItem & {
  funnel_config?: FunnelConfig | null;
  discovery_result?: DiscoveryResult | null;
  confirm_owned_at?: string | null;
  /** `"daily"` while a scheduled run is enabled; null/"" while it is not. */
  scan_schedule?: string | null;
  /** Which kind the scheduled run walks. Defaults server-side to `pixels`. */
  schedule_kind?: string;
  /** When the next scheduled run is due, or null when nothing is scheduled. */
  schedule_next_due_at?: string | null;
};

/** What started a run: the customer, the daily schedule, or setup discovery. */
export type WebsiteRunTrigger = "manual" | "scheduled" | "discovery";

/**
 * One run of THIS website. Same shape as a scan in the account-wide history,
 * plus the trigger — on a website's own page "who started this" is the column
 * that separates a customer's click from the nightly schedule.
 */
export type WebsiteRunListItem = WebsiteScanListItem & {
  trigger: WebsiteRunTrigger | string;
};

export type CreateWebsiteInput = {
  url: string;
  name?: string;
  confirm_owned: boolean;
};

export type UpdateWebsiteInput = {
  name?: string;
  cmp_selected?: CmpSelection;
  funnel_config?: FunnelConfig;
  url?: string;
  /** Required in the SAME request when `url` moves to a new domain. */
  confirm_owned?: boolean;
  /** `"daily"` enables the daily scheduled run; `""` disables it. */
  scan_schedule?: string;
  /** Which kind the scheduled run walks. */
  schedule_kind?: WebsiteScanKind;
};

export type StartWebsiteRunInput = {
  kind: WebsiteScanKind;
  test_campaign_tracking?: boolean;
};

/** What `POST /websites/:id/scans` hands back — enough to navigate to it. */
export type StartWebsiteRunResult = {
  public_id: string;
  status: string;
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** List the account's websites. */
export async function listWebsites(): Promise<WebsiteListItem[]> {
  return apiJson<WebsiteListItem[]>("/websites");
}

/** Fetch one website's full detail (funnel config + discovery proposal). */
export async function getWebsite(publicId: string): Promise<WebsiteDetail> {
  return apiJson<WebsiteDetail>(`/websites/${encodeURIComponent(publicId)}`);
}

/**
 * Create a website. Discovery starts server-side straight away.
 * @throws ApiError with status 409 (a website for this domain already
 * exists), 422 (validation), or 429 (10-website cap or discovery budget).
 */
export async function createWebsite(
  input: CreateWebsiteInput
): Promise<WebsiteDetail> {
  const body: Record<string, unknown> = {
    url: input.url,
    confirm_owned: input.confirm_owned,
  };
  if (input.name !== undefined) body.name = input.name;
  return apiJson<WebsiteDetail>("/websites", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * List this website's own runs, newest first (the backend caps the list).
 * Includes the runs the daily schedule and setup discovery started, not just
 * the ones the customer clicked.
 */
export async function listWebsiteRuns(
  publicId: string
): Promise<WebsiteRunListItem[]> {
  return apiJson<WebsiteRunListItem[]>(
    `/websites/${encodeURIComponent(publicId)}/scans`
  );
}

/**
 * Update a website's settings (ADMIN+).
 * @throws ApiError with status 403 (not an account admin) or 422 (validation,
 * e.g. a funnel URL off the website's own domain, a new domain without
 * `confirm_owned`, or `scan_schedule` on a website whose ownership was never
 * confirmed).
 */
export async function updateWebsite(
  publicId: string,
  input: UpdateWebsiteInput
): Promise<WebsiteDetail> {
  return apiJson<WebsiteDetail>(`/websites/${encodeURIComponent(publicId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/**
 * Re-run discovery for a website.
 * @throws ApiError with status 409 (a scan is already running) or 429
 * (discovery budget for today is spent).
 */
export async function rediscoverWebsite(
  publicId: string
): Promise<WebsiteDetail> {
  return apiJson<WebsiteDetail>(
    `/websites/${encodeURIComponent(publicId)}/discover`,
    { method: "POST" }
  );
}

/**
 * Delete a website (ADMIN+). The website, its settings and its daily schedule
 * are removed; the scan reports it already produced are KEPT and stay
 * reachable as individual scans in the account-wide history.
 *
 * Answers 204 with no body. `apiJson` returns early on 204 and throws
 * `ApiError` WITH `.status`, which the pages need to turn a 403 into the
 * admin-only sentence. (`apiNoContent` has thrown `ApiError` too since
 * 2026-09-27, so either helper would do; this one predates that.)
 *
 * @throws ApiError with status 403 (not an account admin) or 404 (unknown
 * website).
 */
export async function deleteWebsite(publicId: string): Promise<void> {
  await apiJson<void>(`/websites/${encodeURIComponent(publicId)}`, {
    method: "DELETE",
  });
}

/**
 * Start a manual run for this website.
 * @throws ApiError with status 409 (a scan is already running), 422
 * (validation, e.g. a funnel run without funnel URLs) or 429 (daily limit).
 */
export async function startWebsiteRun(
  publicId: string,
  input: StartWebsiteRunInput
): Promise<StartWebsiteRunResult> {
  return apiJson<StartWebsiteRunResult>(
    `/websites/${encodeURIComponent(publicId)}/scans`,
    {
      method: "POST",
      body: JSON.stringify({
        kind: input.kind,
        test_campaign_tracking: input.test_campaign_tracking ?? false,
      }),
    }
  );
}
